import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getClubByIdApi } from "../../api/api";
import { useMediaQuery } from "react-responsive";
import { MOBILE_MAX_WIDTH } from "../../constants/breakpoints";
import { useBirthdayFlow } from "./useBirthdayFlow";
import { formatMoney } from "./birthdayModel";
import { ResultConfirmada, ResultEnviada, ResultShell } from "./BirthdaySteps";
import BirthdayFlowDesktop from "./BirthdayFlowDesktop";
import BirthdayFlowMobile from "./BirthdayFlowMobile";
import "../reservas/reservas.css";
import "./cumpleanos.css";

/** Loads the club of the route and switches between the desktop and mobile layouts, like ClubDetail; the flow itself only mounts once there are birthdays to book. */
const BirthdayFlow = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", club: null, courts: [], error: "" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState((prev) => ({ ...prev, status: "loading", error: "" }));
    getClubByIdApi(clubId)
      .then((response) => {
        if (!active) return;
        const info = response?.data?.info || {};
        setState({
          status: "ready",
          club: info.club || null,
          courts: Array.isArray(info.courts) ? info.courts : [],
          error: "",
        });
      })
      .catch((err) => {
        if (!active) return;
        setState({
          status: "error",
          club: null,
          courts: [],
          error: err?.response?.data?.message || "No pudimos cargar el club.",
        });
      });
    return () => {
      active = false;
    };
  }, [clubId, reloadKey]);

  const backToClub = useCallback(() => navigate(`/canchas/${clubId}`), [navigate, clubId]);

  if (state.status === "loading") {
    return (
      <div className="reservas-root bday">
        <div className="bday-state">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="alert" tone="danger" title="No pudimos cargar el club">
          <p>{state.error}</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={() => setReloadKey((k) => k + 1)}>
              Reintentar
            </button>
            <button type="button" className="r-btn-soft" onClick={() => navigate("/canchas")}>
              Volver a canchas
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  if (state.club && state.club.onlineBookingEnabled !== true) {
    return (
      <div className="reservas-root bday">
        <ResultShell
          icon="info"
          tone="neutral"
          title="Reservas online pausadas"
        >
          <p>
            Este club no está recibiendo reservas online
            en este momento.
          </p>

          <button
            type="button"
            className="r-btn-soft"
            onClick={backToClub}
          >
            Volver al club
          </button>
        </ResultShell>
      </div>
    );
  }

  if (!state.club?.birthdays) {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="info" tone="neutral" title="Este club no toma cumpleaños">
          <p>Por ahora este club no tiene reservas de cumpleaños habilitadas.</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-soft" onClick={state.club ? backToClub : () => navigate("/canchas")}>
              {state.club ? "Volver al club" : "Volver a canchas"}
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  return <BirthdayFlowSteps clubId={clubId} club={state.club} courts={state.courts} onExit={backToClub} />;
};

const BirthdayFlowSteps = ({ clubId, club, courts, onExit }) => {
  const navigate = useNavigate();
  const token = useSelector((s) => s.auth?.accessToken);
  const authUser = useSelector((s) => s.auth?.user || {});
  const location = useLocation();
  // Same login handoff as the court flow: /login returns to this page via `state.from`.
  const onRequireLogin = useCallback(
    () => navigate("/login", { state: { from: location.pathname + location.search } }),
    [navigate, location.pathname, location.search]
  );
  const flow = useBirthdayFlow({ clubId, club, courts, token, authUser, onRequireLogin });
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });
  const { step, config, mode, selectedPackage, amounts } = flow;

  const handleBack = () => {
    if (!flow.goBack()) onExit();
  };

  if (step === "enviada") {
    return (
      <div className="reservas-root bday">
        <ResultEnviada
          email={flow.form.email}
          respondBy={flow.result?.respondBy}
          requestToken={flow.result?.token}
          onViewRequest={() => navigate(`/cumpleanos/solicitud/${flow.result.token}`)}
          onBack={onExit}
        />
      </div>
    );
  }
  if (step === "confirmada") {
    return (
      <div className="reservas-root bday">
        <ResultConfirmada
          result={flow.result}
          email={flow.form.email}
          pkg={selectedPackage}
          date={flow.date}
          slotStart={flow.slotStart}
          onBack={onExit}
        />
      </div>
    );
  }

  const submitLabel =
    mode === "approval"
      ? "Enviar solicitud"
      : config.depositPercent > 0
        ? `Pagar seña de ${formatMoney(amounts.deposit)}`
        : "Confirmar cumple";

  const Layout = isMobile ? BirthdayFlowMobile : BirthdayFlowDesktop;
  return <Layout flow={flow} club={club} courts={courts} submitLabel={submitLabel} onBack={handleBack} />;
};

export default BirthdayFlow;
